'use client';

import { useQueryState } from 'nuqs';
import { useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../tabs';

interface UrlTabsProps {
  defaultValue: string;
  paramName?: string;
  children: React.ReactNode;
  className?: string;
  tabsListClassName?: string;
}

/**
 * URL-based Tabs component that syncs the active tab with URL search parameters.
 * Uses nuqs for shallow routing to avoid triggering server-side page rerenders.
 *
 * @param defaultValue - The default tab value when no URL parameter is present
 * @param paramName - The URL parameter name to use (defaults to 'tab')
 * @param children - TabsList and TabsContent components
 */
export function UrlTabs({ defaultValue, paramName = 'tab', children, className }: UrlTabsProps) {
  const [currentTab, setCurrentTab] = useQueryState(paramName, {
    defaultValue,
    shallow: true, // Prevents server-side rerenders
    scroll: false, // Don't scroll on tab change
    history: 'push', // Use push for browser history
    clearOnDefault: true, // Remove param when defaultValue is selected
  });

  const handleTabChange = useCallback(
    (value: string) => {
      setCurrentTab(value);
    },
    [setCurrentTab]
  );

  return (
    <Tabs value={currentTab} onValueChange={handleTabChange} className={className}>
      {children}
    </Tabs>
  );
}

interface UrlTabsListProps {
  children: React.ReactNode;
  className?: string;
}

export function UrlTabsList({ children, className }: UrlTabsListProps) {
  return <TabsList className={className}>{children}</TabsList>;
}

interface UrlTabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function UrlTabsTrigger({ value, children, className }: UrlTabsTriggerProps) {
  return (
    <TabsTrigger value={value} className={className}>
      {children}
    </TabsTrigger>
  );
}

interface UrlTabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function UrlTabsContent({ value, children, className }: UrlTabsContentProps) {
  return (
    <TabsContent value={value} className={className}>
      {children}
    </TabsContent>
  );
}
